import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { authFetch } from '../pages/admin/constants';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface AgentInteraction {
  agentId: string;
  lastSeenVersion: string;
  firstInteractionDate: string;
  lastInteractionDate: string;
  interactionCount: number;
  lastInteractionType: string;
}

interface AgentInteractionsState {
  interactions: Record<string, AgentInteraction>;
  loading: boolean;
  error: string | null;
}

// LocalStorage keys for fallback/cache
const STORAGE_KEY_SEEN = 'ask-aldo-seen-agents';
const STORAGE_KEY_VERSIONS = 'ask-aldo-seen-versions';

// Helper to get from localStorage (fallback)
const getLocalStorageInteractions = (): Record<string, AgentInteraction> => {
  try {
    const seenAgents = JSON.parse(localStorage.getItem(STORAGE_KEY_SEEN) || '[]');
    const seenVersions = JSON.parse(localStorage.getItem(STORAGE_KEY_VERSIONS) || '{}');
    
    const interactions: Record<string, AgentInteraction> = {};
    seenAgents.forEach((agentId: string) => {
      interactions[agentId] = {
        agentId,
        lastSeenVersion: seenVersions[agentId] || '1.0.0',
        firstInteractionDate: new Date().toISOString(),
        lastInteractionDate: new Date().toISOString(),
        interactionCount: 1,
        lastInteractionType: 'unknown'
      };
    });
    return interactions;
  } catch {
    return {};
  }
};

// Helper to save to localStorage (cache/fallback)
const saveToLocalStorage = (agentId: string, version: string) => {
  try {
    const seenAgents = JSON.parse(localStorage.getItem(STORAGE_KEY_SEEN) || '[]');
    const seenVersions = JSON.parse(localStorage.getItem(STORAGE_KEY_VERSIONS) || '{}');
    
    if (!seenAgents.includes(agentId)) {
      seenAgents.push(agentId);
    }
    seenVersions[agentId] = version;
    
    localStorage.setItem(STORAGE_KEY_SEEN, JSON.stringify(seenAgents));
    localStorage.setItem(STORAGE_KEY_VERSIONS, JSON.stringify(seenVersions));
  } catch {
    // Ignore localStorage errors
  }
};

export const useAgentInteractions = () => {
  const [state, setState] = useState<AgentInteractionsState>({
    interactions: {},
    loading: true,
    error: null
  });

  // Fetch interactions from API
  const fetchInteractions = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await authFetch(`${API_BASE_URL}/api/user/agent-interactions`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Merge with localStorage for immediate display
      const localInteractions = getLocalStorageInteractions();
      const mergedInteractions = { ...localInteractions, ...data.interactions };
      
      setState({
        interactions: mergedInteractions,
        loading: false,
        error: null
      });
      
      return mergedInteractions;
    } catch (error) {
      console.warn('Failed to fetch agent interactions from API, using localStorage:', error);
      
      // Fall back to localStorage
      const localInteractions = getLocalStorageInteractions();
      setState({
        interactions: localInteractions,
        loading: false,
        error: null // Don't show error to user, just use fallback
      });
      
      return localInteractions;
    }
  }, []);

  // Record an interaction
  const recordInteraction = useCallback(async (
    agentId: string, 
    version: string, 
    interactionType: 'conversation' | 'question_click' | 'patch_notes_view' = 'conversation'
  ) => {
    // Optimistically update local state and localStorage
    saveToLocalStorage(agentId, version);
    
    setState(prev => ({
      ...prev,
      interactions: {
        ...prev.interactions,
        [agentId]: {
          agentId,
          lastSeenVersion: version,
          firstInteractionDate: prev.interactions[agentId]?.firstInteractionDate || new Date().toISOString(),
          lastInteractionDate: new Date().toISOString(),
          interactionCount: (prev.interactions[agentId]?.interactionCount || 0) + 1,
          lastInteractionType: interactionType
        }
      }
    }));
    
    // Send to API (fire and forget, don't block UI)
    try {
      await authFetch(`${API_BASE_URL}/api/user/agent-interactions`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agentId,
          version,
          interactionType
        })
      });
    } catch (error) {
      console.warn('Failed to record agent interaction to API:', error);
      // Already saved to localStorage, so user experience is not affected
    }
  }, []);

  // Check if agent has been seen
  const hasSeenAgent = useCallback((agentId: string): boolean => {
    return !!state.interactions[agentId];
  }, [state.interactions]);

  // Get last seen version for an agent
  const getLastSeenVersion = useCallback((agentId: string): string | null => {
    return state.interactions[agentId]?.lastSeenVersion || null;
  }, [state.interactions]);

  // Get the ISO date of the user's most recent interaction with an agent
  const getLastInteractionDate = useCallback((agentId: string): string | null => {
    return state.interactions[agentId]?.lastInteractionDate || null;
  }, [state.interactions]);

  // Check if agent has update (version mismatch)
  const hasUpdate = useCallback((agentId: string, currentVersion: string): boolean => {
    const lastSeen = state.interactions[agentId]?.lastSeenVersion;
    return !!lastSeen && lastSeen !== currentVersion;
  }, [state.interactions]);

  // Load on mount
  useEffect(() => {
    fetchInteractions();
  }, [fetchInteractions]);

  return {
    interactions: state.interactions,
    loading: state.loading,
    error: state.error,
    recordInteraction,
    hasSeenAgent,
    getLastSeenVersion,
    getLastInteractionDate,
    hasUpdate,
    refresh: fetchInteractions
  };
};

export default useAgentInteractions;

















